// Обёртка над Telegram WebApp. Всё общение с клиентом Telegram — только здесь,
// чтобы игра открывалась и в обычном браузере (для отладки), не падая.

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name?: string } };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  version: string;
  platform: string;
  isExpanded: boolean;
  expand: () => void;
  ready: () => void;
  close: () => void;
  disableVerticalSwipes?: () => void;
  requestFullscreen?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void; offClick: (cb: () => void) => void };
  MainButton: {
    setText: (text: string) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  openTelegramLink?: (url: string) => void;
  shareMessage?: (id: string, cb?: (sent: boolean) => void) => void;
  showAlert?: (message: string, cb?: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

// Telegram кладёт подписанные данные не только в SDK, но и в хеш адреса.
// Читаем его сразу при загрузке: дальше приложение может хеш потерять, а
// заход должен работать даже тогда, когда скрипт SDK не доехал.
const hashParams = ((): Record<string, string> => {
  try {
    let raw = window.location.hash.replace(/^#/, "");
    if (!raw) return {};
    // У ссылок вида t.me/бот/приложение параметры идут после пути:
    // #slug?tgWebAppData=… Разбираем так же, как это делает сам SDK.
    const query = raw.indexOf("?");
    if (query >= 0) raw = raw.slice(query + 1);
    else if (!raw.includes("=")) return {};
    return Object.fromEntries(new URLSearchParams(raw));
  } catch {
    return {};
  }
})();

/** Подписанные данные запуска: из SDK, а если его нет — из адреса */
export const initData = (): string => tg()?.initData || hashParams.tgWebAppData || "";

/**
 * С чем приложение открылось — уходит вместе с запросом на вход и попадает
 * в журнал, когда войти не удалось. Значений параметров здесь нет: только их
 * имена, подпись в журнал не пишем.
 */
export function launchInfo(): {
  sdk: boolean;
  params: string[];
  platform: string;
  version: string;
} {
  const app = tg();
  return {
    sdk: Boolean(app),
    params: Object.keys(hashParams).slice(0, 20),
    platform: app?.platform ?? "нет",
    version: app?.version ?? "нет",
  };
}

/** Запущено ли внутри Telegram — вне его игра работает в режиме отладки */
export const inTelegram = (): boolean => Boolean(initData());

export function initTelegram(): void {
  const app = tg();
  if (!app) return;

  app.ready();
  app.expand();
  app.disableVerticalSwipes?.();
  app.setHeaderColor?.("#05070b");
  app.setBackgroundColor?.("#05070b");
  document.documentElement.dataset.tg = app.colorScheme;
}

export function haptic(type: "light" | "medium" | "heavy" = "light"): void {
  tg()?.HapticFeedback?.impactOccurred(type);
}

export function notify(type: "error" | "success" | "warning"): void {
  tg()?.HapticFeedback?.notificationOccurred(type);
}

export function useBackButton(handler: (() => void) | null): () => void {
  const app = tg();
  if (!app) return () => undefined;
  if (!handler) {
    app.BackButton.hide();
    return () => undefined;
  }
  app.BackButton.onClick(handler);
  app.BackButton.show();
  return () => {
    app.BackButton.offClick(handler);
    app.BackButton.hide();
  };
}

/** Отправить ссылку другу средствами Telegram, если клиент это умеет */
export function shareLink(url: string, text: string): void {
  const app = tg();
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  if (app?.openTelegramLink) app.openTelegramLink(share);
  else window.open(share, "_blank");
}

export const startParam = (): string | undefined =>
  tg()?.initDataUnsafe?.start_param ?? hashParams.tgWebAppStartParam ?? undefined;
